/**
 * PortalLayout — Layout wrapper for portal pages
 *
 * Renders the portal-specific sidenav (from portal navigation config)
 * and the content area for the current portal page.
 *
 * This component:
 *   - Activates the correct portal based on URL slug
 *   - Renders portal navigation items in the main sidenav
 *   - Routes to PortalPageRenderer for dynamic pages
 *   - Respects CASL permissions for navigation items
 */

import React, { useEffect, useMemo } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import { useMaterialUIController } from "context";
import { usePortalContext } from "context/PortalContext";
import PortalPageRenderer from "./PortalPageRenderer";
import logger from "services/logger";

function PortalLayout() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { portalSlug, pageSlug } = useParams();
  const portalCtx = usePortalContext();
  const { activePortal, setActivePortal, loading } = portalCtx;

  // Activate portal when slug changes
  useEffect(() => {
    if (portalSlug && (!activePortal || activePortal.slug !== portalSlug)) {
      setActivePortal(portalSlug);
    }
  }, [portalSlug, activePortal, setActivePortal]);

  // Loading state
  if (loading) {
    return (
      <KDBox display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={48} sx={{ color: "primary.main" }} />
      </KDBox>
    );
  }

  // Portal not found
  if (!activePortal) {
    return (
      <KDBox p={3} display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <KDTypography variant="h4" color={darkMode ? "white" : "dark"} gutterBottom>
          Portal Not Found
        </KDTypography>
        <KDTypography variant="body1" sx={{ color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)" }}>
          The portal "{portalSlug}" does not exist or you do not have access.
        </KDTypography>
      </KDBox>
    );
  }

  // If no page slug, redirect to first navigation item
  if (!pageSlug) {
    const firstNav = (activePortal.navigation || [])[0];
    if (firstNav) {
      return <Navigate to={firstNav.route || `/portal/${portalSlug}/${firstNav.pageId || firstNav.id}`} replace />;
    }
    // No navigation items — show empty portal
    return (
      <KDBox p={3}>
        <KDTypography variant="h5" color={darkMode ? "white" : "dark"}>
          {activePortal.name}
        </KDTypography>
        <KDTypography variant="body2" sx={{ color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", mt: 1 }}>
          This portal has no pages configured yet.
        </KDTypography>
      </KDBox>
    );
  }

  // Find the page to render
  const targetPage = (activePortal.pages || []).find(
    (p) => p.slug === pageSlug || p.id === pageSlug
  );

  if (!targetPage) {
    return (
      <KDBox p={3}>
        <KDTypography variant="h5" color={darkMode ? "white" : "dark"}>
          Page Not Found
        </KDTypography>
        <KDTypography variant="body2" sx={{ color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", mt: 1 }}>
          The page "{pageSlug}" was not found in this portal.
        </KDTypography>
      </KDBox>
    );
  }

  return <PortalPageRenderer pageId={targetPage.id} />;
}

export default PortalLayout;
